import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { DraftInvoiceRequest } from '@/api/handlers/resources/purchases/requests';
import type { GetDraftInvoiceResponse } from '@/api/handlers/resources/purchases/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import {
    invalidateDraftInvoiceCaches,
    upsertDraftInvoiceCaches,
} from './draftInvoiceCache';

export type UpdateDraftInvoiceVariables = {
    id: string;
    body: DraftInvoiceRequest;
};

/**
 * Subsequent saves of a draft (`PUT /purchases/draft-invoices/{id}` — full
 * replace). On success the echoed draft is merged into the drafts-list row
 * and replaces the detail cache (a resume reads the fresh save without a
 * refetch); the list + summary are then invalidated to reconcile the
 * server-computed fields. Never rejects — consumers branch on
 * `result.success`.
 */
export const useUpdateDraftInvoiceMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetDraftInvoiceResponse>,
        Error,
        UpdateDraftInvoiceVariables
    >({
        mutationFn: ({ id, body }) =>
            apiClient.purchases.updateDraftInvoice(id, body),
        onSuccess: (result) => {
            if (result.success) {
                upsertDraftInvoiceCaches(queryClient, result.response);
                invalidateDraftInvoiceCaches(queryClient);
            }
        },
    });
};
